import {
  SIGNATURE_REQUEST_TRANSITION_STATES,
  SignatureRequest,
  SignatureRequestTransition,
} from 'lib/models/signature-request'
import { ProbateSignatureRequestDocument } from 'lib/models/probate-estate'

export type SignatureRequestWithDocument = SignatureRequest & {
  documentName: ProbateSignatureRequestDocument
}

export function getMostRecentTransition(
  signatureRequest: SignatureRequest
): SignatureRequestTransition | null {
  return signatureRequest.transitions?.find((t) => t.mostRecent) ?? null
}

export function isSignatureRequestInactive(
  signatureRequest: SignatureRequest
): boolean {
  const mostRecent = getMostRecentTransition(signatureRequest)
  const state = mostRecent?.toState
  return (
    state === SIGNATURE_REQUEST_TRANSITION_STATES.CANCELLED ||
    state === SIGNATURE_REQUEST_TRANSITION_STATES.DECLINED
  )
}

export function getActiveSignatureRequest(
  signatureRequests: SignatureRequestWithDocument[] = [],
  documentName: ProbateSignatureRequestDocument,
  type: 'manual' | 'auto'
): SignatureRequestWithDocument | null {
  const filtered = signatureRequests
    .filter((signatureRequest) => {
      const isDocumentMatch = signatureRequest.documentName === documentName
      const isTypeMatch =
        type === 'manual'
          ? signatureRequest.externalId === null
          : signatureRequest.externalId !== null
      return isDocumentMatch && isTypeMatch
    })
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

  const mostRecent = filtered.at(0)
  if (!mostRecent || isSignatureRequestInactive(mostRecent)) {
    return null
  }
  return mostRecent
}

export function getSignatureRequestDates(
  signatureRequest: SignatureRequest | null
): {
  sentAt: Date | null
  receivedAt: Date | null
} {
  if (!signatureRequest || isSignatureRequestInactive(signatureRequest)) {
    return { sentAt: null, receivedAt: null }
  }

  const mostRecent = getMostRecentTransition(signatureRequest)

  // If signed, return the creation date as sentAt and transition date as receivedAt
  if (mostRecent?.toState === SIGNATURE_REQUEST_TRANSITION_STATES.SIGNED) {
    return {
      sentAt: new Date(signatureRequest.createdAt),
      receivedAt: new Date(mostRecent.createdAt),
    }
  }

  // Otherwise it's awaiting_signatures - get the last sent transition
  const lastSentTransition = signatureRequest.transitions
    ?.filter(
      (t) =>
        t.toState === SIGNATURE_REQUEST_TRANSITION_STATES.AWAITING_SIGNATURES
    )
    .at(-1)

  return {
    sentAt: lastSentTransition ? new Date(lastSentTransition.createdAt) : null,
    receivedAt: null,
  }
}
